import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Paper,
    SvgIcon,
    Typography,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import { t } from "i18next";

import { ReactComponent as ImportIcon } from "../../../../assets/images/import.svg";
import { styles } from "./EmailedFiles.styles";

import CircularLoaderFullPage from "src/components/common/CircularLoaderFullPage/CircularLoaderFullPage";
import SecureSyncTable from "src/components/common/SecureSyncTable/SecureSyncTable";
import { getInventoryDataService } from "src/services/inventoryService";
import { getDisplayFormat } from "src/utils/dateHelper";

import { dateFormats } from "src/constants/dateFormatConstants";
import { yesFlag } from "src/constants/serviceConstants";
import { convertToLocaleUpperCase } from "src/utils/commonHelper";
import { downloadFileFromFB, getApiErrorMessage } from "src/services/utils";
import SecureSyncIconTooltip from "src/components/common/SecureSyncIconTooltip/SecureSyncIconTooltip";
import InfoCircle from "src/components/Icons/InfoCircle/InfoCircle";

const EmailedFiles = (props) => {
    const [isLoading, setLoading] = useState(false);
    const [rows, setRow] = useState([]);
    const theme = useTheme();

    const columns = [
        {
            field: "edit",
            headerName: "",
            width: 60,
            sortable: false,
            renderCell: (param) =>
                param.gsc ? (
                    <Box>
                        <IconButton
                            onClick={() => {
                                downloadFile(param);
                            }}
                        >
                            <SvgIcon component={ImportIcon} />
                        </IconButton>
                    </Box>
                ) : param.error_code ? (
                    <SecureSyncIconTooltip
                        title={
                            <>
                                <Typography>
                                    <Box component="span">{getApiErrorMessage(param?.error_code)}</Box>
                                </Typography>
                            </>
                        }
                        placement="right"
                        icon={<InfoCircle fill={theme.palette.im_red["500"]} />}
                    />
                ) : (
                    <></>
                ),
        },
        { field: "formattedDate", headerName: t("formFields.requestedDateTime"), flex: 1, sortable: false },
        { field: "status", headerName: t("common.status"), flex: 1, sortable: false },
        { field: "fileName", headerName: t("downloadInventory.fileFormat"), flex: 1, sortable: false },
        { field: "filter", headerName: t("downloadInventory.inventoryData"), flex: 1, sortable: false },
        {
            field: "includeInTransitNotAtimFlag",
            headerName: t("downloadInventory.includeMediaInTransitAndNotAtIronMountain"),
            flex: 1,
            sortable: false,
        },
        {
            field: "includeColumnHeadingsFlag",
            headerName: t("downloadInventory.includeColumnHeaders"),
            flex: 1,
            sortable: false,
        },
    ];

    const downloadFile = async (param) => {
        const { gsc, fullFileName } = param;
        setLoading(true);
        await downloadFileFromFB({
            fbFilePath: gsc,
            fileName: fullFileName ? fullFileName : "NA",
            onComplete: () => {
                setLoading(false);
            },
        });
    };
    const getRequestedFiles = async () => {
        setLoading(true);
        try {
            const data = await getInventoryDataService();
            const displayFormat = getDisplayFormat();
            const inventoryData = data.docs.map((doc) => {
                const data = doc.data(); // Call it once
                const filter =
                    data.filter === "1"
                        ? t("common.openMedia")
                        : data.filter === "2"
                          ? t("common.container")
                          : convertToLocaleUpperCase(t("common.all"));
                return {
                    id: doc.id,
                    ...data,
                    fullFileName: data.fileName,
                    formattedDate: dayjs(data.docCreateTime.toDate()).format(
                        `${displayFormat} ${dateFormats.timeFormat24}`
                    ),
                    fileName: data?.fileName && data?.fileName.endsWith(".csv") ? "CSV" : "TXT",
                    filter: filter,
                    includeInTransitNotAtimFlag:
                        data.includeInTransitNotAtimFlag === yesFlag ? t("common.yes") : t("common.no"),
                    includeColumnHeadingsFlag:
                        data.includeColumnHeadingsFlag === yesFlag ? t("common.yes") : t("common.no"),
                    status: data.failedAt ? t("downloadInventory.FAILED") : t(`downloadInventory.${data.status}`),
                    gsc: data?.gcsPath,
                };
            });
            inventoryData.sort((a, b) => {
                const dateA = a.docCreateTime.toDate();
                const dateB = b.docCreateTime.toDate();
                return dateB - dateA;
            });
            setRow(inventoryData);
        } catch (error) {
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <CircularLoaderFullPage show={isLoading} />
            <Accordion
                onChange={(event, expanded) => {
                    if (expanded) {
                        getRequestedFiles();
                    }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="authorization-verification-content"
                    id="authorization-verification"
                >
                    <Typography variant="body1" sx={styles.headerTypography}>
                        {t("downloadInventory.downloadInventoryFiles")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box>
                        <Box style={styles.tableBox}>
                            <Paper>
                                <SecureSyncTable
                                    propStyles={styles.dataGrid}
                                    rows={rows}
                                    columns={columns}
                                    noDataMessage={t("messages.noRecordsFound")}
                                    getRowId={(row) => row.customerId}
                                    sx={styles.dataGrid}
                                />
                            </Paper>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

EmailedFiles.propTypes = {
    count: PropTypes.number,
};

export default EmailedFiles;
